// src/components/SymptomsSummaryCard.tsx
import React, { useState, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSymptomLogs } from "../hooks/useSymptomLogs";
import { SymptomWeekRow } from "./SymptomWeekRow";
import { BleedingLogModal } from "./BleedingLogModal";
import { SymptomDayModal } from "./SymptomDayModal";
import { getLast7DaysRange, type SymptomDaySummary } from "../utils/symptomUtils";
import { symptomConfigs } from "../config/symptomsConfig";
import { useLessons } from "../hooks/useLessons";
import { useWeekContext } from "../context/WeekContext";
import type { SymptomKey } from "../types/recovery";

// ── Symptom key config ─────────────────────────────────────────────────────────
const ALL_SYMPTOM_KEYS: SymptomKey[] = [
  "perinealPain", "cSectionWound", "fatigue", "breastfeedingPain",
  "bladderControl", "bowelFunction", "pelvicHeaviness", "painAndMovement",
];
const WEEK_SYMPTOMS: Record<number, SymptomKey[]> = {
  1: ["perinealPain", "cSectionWound", "fatigue"],
  2: ["breastfeedingPain", "fatigue", "pelvicHeaviness"],
  3: ["painAndMovement", "fatigue", "pelvicHeaviness"],
  4: ["bladderControl", "bowelFunction", "fatigue", "pelvicHeaviness", "painAndMovement"],
  5: ["fatigue", "pelvicHeaviness", "painAndMovement"],
  6: ALL_SYMPTOM_KEYS,
};
const clampWeek = (w: number) => Math.min(Math.max(w, 1), 6);

type Props = {
  userId: string;
  currentWeek?: number;
};

const SymptomsSummaryCard: React.FC<Props> = ({ userId, currentWeek = 1 }) => {
  const { days, description, todaySummary, loadState } = useSymptomLogs(userId);
  const { birthType } = useLessons();
  const { unlockedWeek } = useWeekContext();
  const navigate = useNavigate();
  const location = useLocation();

  // Modal state: bleeding detail
  const [bleedingDay, setBleedingDay] = useState<SymptomDaySummary | null>(null);
  
  // Modal state: single symptom detail (keeps track of both day and which key was clicked)
  const [symptomDayState, setSymptomDayState] = useState<{
    day: SymptomDaySummary;
    symptomKey: SymptomKey;
  } | null>(null);

  const dateRangeTitle = useMemo(() => getLast7DaysRange(days), [days]);

  const weekSymptomKeys: SymptomKey[] = useMemo(() => {
    const baseKeys = WEEK_SYMPTOMS[clampWeek(currentWeek)] ?? ALL_SYMPTOM_KEYS;
    return baseKeys.filter((key) => {
      if (key === "cSectionWound" && birthType !== "csection") return false;
      if (key === "perinealPain" && birthType === "csection") return false;
      return true;
    });
  }, [currentWeek, birthType]);

  // Bleeding row handlers
  const handleBleedingLoggedDayClick = useCallback((day: SymptomDaySummary) => {
    setBleedingDay(day);
  }, []);

  const handleBleedingEmptyDayClick = useCallback((day: SymptomDaySummary) => {
    // Navigate directly to bleeding check-in
    navigate("/recovery/checkins/bleeding", {
      state: { dateId: day.dateId, week: unlockedWeek, from: "dashboard", backgroundLocation: location },
    });
  }, [navigate, unlockedWeek, location]);

  // Symptom row empty day handler
  const handleSymptomEmptyDayClick = useCallback((day: SymptomDaySummary) => {
    // Navigate directly to symptoms check-in
    navigate("/recovery/checkins/symptoms", {
      state: { dateId: day.dateId, week: unlockedWeek, from: "dashboard", backgroundLocation: location },
    });
  }, [navigate, unlockedWeek, location]);


  if (loadState === "loading") {
    return (
      <article className="dashboard-card" style={{ 
        minHeight: '300px', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#fcf9f7'
      }}>
        {/* Spinner */}
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid rgba(30, 94, 91, 0.1)',
          borderTop: '4px solid #1e5e5b',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          marginBottom: '20px',
          boxSizing: 'border-box'
        }} />

        {/* Branding */}
        <div style={{
          fontFamily: 'DM Sans, sans-serif',
          textAlign: 'center'
        }}>
          <p style={{
            color: '#0c2624',
            fontSize: '14px',
            fontWeight: '600',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            margin: 0,
            animation: 'pulse 1.5s ease-in-out infinite'
          }}>
            BodyOwn
          </p>
        </div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </article>
    );
  }

  return (
    <>
      <article className="dashboard-card dashboard-card-symptoms" style={{ position: "relative" }}>
        {/* ── Symptom Trends ─────────────────────────────────────────────── */}
        <div className="dashboard-card-header">
          <div className="dashboard-card-header-main">
            <h3 className="dashboard-card-subtitle">Your symptom trends</h3>
            <div className="tab_1">Last 7 days</div>
          </div>
        </div>

        <div className="dashboard-card-body-PD">
          {weekSymptomKeys.length > 0 ? (
            weekSymptomKeys.map((key) => {
              const config = symptomConfigs[key as keyof typeof symptomConfigs];
              if (!config) return null;
              const shortLabel = config.label
                .replace(" or discomfort", "")
                .replace(" / leaks", "")
                .replace(" / sleep quality", "");
              return (
                <SymptomWeekRow
                  key={key}
                  title={shortLabel}
                  days={days}
                  mode="symptom"
                  symptomKey={key}
                  onDayClick={(day) => setSymptomDayState({ day, symptomKey: key })}
                  onEmptyDayClick={handleSymptomEmptyDayClick}
                />
              );
            })
          ) : (
            <div className="py-6 text-center">
              <p className="text-xs text-gray-400 font-dm-sans italic">
                No specific symptoms to track for your recovery profile this week.
              </p>
            </div>
          )}
          <div className="WeekSummary">{description}</div>
        </div>

        <div className="symptoms-divider" />

        {/* ── Bleeding Log ───────────────────────────────────────────────── */}
        <div className="dashboard-card-header">
          <div className="dashboard-card-header-main">
            <h3 className="dashboard-card-subtitle">Your bleeding log</h3>
            <div className="tab_1">Last 7 days</div>
          </div>
        </div>

        <SymptomWeekRow
          title={dateRangeTitle}
          days={days}
          mode="bleeding"
          onDayClick={handleBleedingLoggedDayClick}
          onEmptyDayClick={handleBleedingEmptyDayClick}
        />

        <div className="WeekSummary">{todaySummary}</div>

      </article>

      {/* ── Modals ──────────────────────────────────────────────────────────── */}
      {bleedingDay && (
        <BleedingLogModal day={bleedingDay} onClose={() => setBleedingDay(null)} />
      )}
      {symptomDayState && (
        <SymptomDayModal
          day={symptomDayState.day}
          symptomKey={symptomDayState.symptomKey}
          onClose={() => setSymptomDayState(null)}
        />
      )}
    </>
  );
};

export default SymptomsSummaryCard;